import { useEffect } from 'react';
import { motion, PanInfo, useAnimation } from 'framer-motion';
import { Model } from '@client:common/Model';
import { IconButton, LinkButton } from '@client:common/button';
import { Icon } from '@client:common/icon';
import { Theme } from '@client:common/Theme';

import './ModelDrawer.light.scss';
import './ModelDrawer.dark.scss';

interface Props {
  model: Model;
  show: boolean;
  toggle: () => void;
  currentTheme: Theme;
}

export function ModelDrawer(props: Props) {
  const { model, show, toggle } = props;
  const controls = useAnimation();

  const handleDragEnd = async (_: any, info: PanInfo) => {
    const offset = info.offset.x;
    if (offset > 100) {
      await controls.start({ x: '100%' });
      toggle();
    } else {
      controls.start({ x: 0 });
    }
  };

  useEffect(() => {
    if (show) {
      controls.start({ x: 0 });
    } else {
      controls.start({ x: '100%' });
    }
  }, [show, controls]);

  const isValidUrl = (urlString: string) => {
    let url;

    try {
      url = new URL(urlString);
    } catch {
      return false;
    }

    return url.protocol === 'http:' || url.protocol === 'https:';
  };

  return (
    <motion.div
      className='side-drawer'
      initial={{ x: '100%' }}
      animate={controls}
      drag="x"
      dragConstraints={{ left: 0, right: window.innerWidth }}
      dragElastic={0.2}
      onDragEnd={handleDragEnd}
      style={{ position: 'fixed' }}
    >
      <div className='drawer-wrapper'>
        <div className='drawer-header-container'>
          <div className='drawer-header-top'>
            <div className='header-top_row'>
              <h1 className='model-title' dangerouslySetInnerHTML={{ __html: model.acronym ? model.acronym : model.name }}></h1>
              <IconButton
                style='basic'
                color='primary'
                onClick={toggle}>
                <Icon
                  className='close-icon'
                  icon='close' />
              </IconButton>
            </div>
            <div className='header-top_row'>
              <div>
                {model.link && (
                  <LinkButton
                    analyticsCategory="Dashboard"
                    analyticsAction={`opened_model_in_new_tab_dashboard_${model.acronym}`}
                    analyticsLabel={`Opened model in new tab from dashboard. Model: ${model.acronym}`}
                    openInNewTab={true}
                    to={model.link}
                    style='basic'
                    color='primary'>
                    <Icon icon='open_in_new' /> OPEN IN NEW TAB
                  </LinkButton>
                )}

                {(model.category === 'Price-taker Valuation' || model.category === 'Production Cost Modeling') && (
                  <LinkButton
                    to={`/pages/model-comparator?category=${model.category}`}
                    style='basic'
                    color='primary'>
                    <Icon icon='open_in_new' /> VIEW IN COMPARISON TABLE
                  </LinkButton>
                )}
              </div>
            </div>
          </div>
          <div className='drawer-header-bottom'>
            <figure className={'model-card__image'}>
              <div className='model-card__image-container'>
                {props.model.imageUrl && props.model.imageUrl_dark ? (
                  props.currentTheme === 'dark' ? (
                    <img
                      className='model-image'
                      src={props.model.imageUrl_dark} />
                  ) : (
                    <img
                      className='model-image'
                      src={props.model.imageUrl} />
                  )
                ) : props.model.imageUrl ? (
                  <img
                    className='model-image'
                    src={props.model.imageUrl} />
                ) : (
                  <p className='acronym-logo'>{props.model.acronym}</p>
                )}
              </div>
            </figure>
          </div>
        </div>
        <div className='drawer-body-container'>
          <div className='drawer-body-item'>
            <p className='body-item-header'>Model/Tool</p>
            <p className='body-item-description' dangerouslySetInnerHTML={{ __html: model.name }}></p>
          </div>
          {model.acronym && (
            <div className='drawer-body-item'>
              <p className='body-item-header'>Acronym</p>
              <p className='body-item-description' dangerouslySetInnerHTML={{ __html: model.acronym }}></p>
            </div>
          )}
          <div className='drawer-body-item'>
            <p className='body-item-header'>Developer</p>
            <div className='body-item-description institution'>
              {model.institution.map((item, index) => (
                <span key={index} dangerouslySetInnerHTML={{ __html: item }}></span>
              ))}
            </div>
          </div>
          <div className='drawer-body-item'>
            <p className='body-item-header'>Primary Use</p>
            <p className='body-item-description' dangerouslySetInnerHTML={{ __html: model.primaryUse }}></p>
          </div>
          <div className='drawer-body-item'>
            <p className='body-item-header'>Energy Sector</p>
            <p className='body-item-description'>{model.energySector}</p>
          </div>
          <div className='drawer-body-item'>
            <p className='body-item-header'>Category</p>
            <p className='body-item-description'>{model.category}</p>
          </div>
          <div className='drawer-body-item'>
            <p className='body-item-header'>Description</p>
            <p className='body-item-description' dangerouslySetInnerHTML={{ __html: model.description }}></p>
          </div>
          <div className='drawer-body-item'>
            <p className='body-item-header'>Model Type</p>
            <p className='body-item-description'>{model.type}</p>
          </div>
          {model.availability && (
            <div className='drawer-body-item'>
              <p className='body-item-header'>Availability</p>
              <p className='body-item-description'>{model.availability}</p>
            </div>
          )}
          {model.publications && (
            <div className='drawer-body-item'>
              <p className='body-item-header'>Publication</p>
              <p className='body-item-description'>
                {isValidUrl(model.publications) ? (
                  <a
                    href={model.publications}
                    target='_blank'
                    rel='noreferrer'>
                    {model.publications}
                  </a>
                ) : (
                  model.publications
                )}
              </p>
            </div>
          )}
          {model.documentation && (
            <div className='drawer-body-item'>
              <p className='body-item-header'>Documentation</p>
              <p className='body-item-description'>
                {isValidUrl(model.documentation) ? (
                  <a
                    href={model.documentation}
                    target='_blank'
                    rel='noreferrer'>
                    {model.documentation}
                  </a>
                ) : (
                  model.documentation
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
